import {
  ProductionItem,
  PaginationData,
  ProductionItemDetail,
  SeedBatch,
  SeedTicket,
} from 'constants/interface';
import { ProductionItemSeedStatusEnum } from 'constants/order';
// import { InventoryTypeEnum } from 'constants/inventory';
// import {
//   ProductionItemApiStatus,
// } from 'constants/order';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface GetProductionItemList extends PaginationData {
  // status?: ProductionItemApiStatus
}

interface SeedReservationData {
  batches: {
    batch: string
    quantity: number
  }[]
}

export interface UpdateSeedReservationData {
  id: string
  data: SeedReservationData
}

interface SeedTicketData {
  requestQuantity: number
}

export interface CreateSeedTicketData {
  productionItemId: string
  data: SeedTicketData
}

export interface ProductionItemApi {
  getProductionItem(productionItemId: string): Promise<ProductionItemDetail>
  getProductionItemList(filter: GetProductionItemList): Promise<PaginatedResponse<ProductionItem>>
  getProductionItemSeedStatus(productionItemId: string): Promise<ProductionItemSeedStatusEnum>
  getProductionItemAvailableSeedBatches(productionItemId: string): Promise<SeedBatch[]>
  updateSeedReservation(data: UpdateSeedReservationData): Promise<ProductionItem>
  createSeedTicket(data: CreateSeedTicketData): Promise<SeedTicket>
}

const productionItemApiBuilder = (): ProductionItemApi => {
  const getProductionItem = async (
    productionItemId: string,
  ): Promise<ProductionItemDetail> => {
    const result = await requestClient.get(`/production-items/${productionItemId}`);
    const productionItem: ProductionItemDetail = result.data as ProductionItemDetail;
    return productionItem;
  };

  const getProductionItemList = async (
    filterData: GetProductionItemList,
  ): Promise<PaginatedResponse<ProductionItem>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/production-items', params);
    const pagination = getPaginationHeaders(result);
    const productionItemList: ProductionItem[] = result.data as ProductionItem[];
    return {
      pagination,
      data: productionItemList,
    };
  };

  const getProductionItemSeedStatus = async (
    productionItemId: string,
  ): Promise<ProductionItemSeedStatusEnum> => {
    const result = await requestClient.get(`/production-items/${productionItemId}/seed-status`);
    const { status } = result
      .data as { status: ProductionItemSeedStatusEnum };
    return status;
  };

  const getProductionItemAvailableSeedBatches = async (
    productionItemId: string,
  ): Promise<SeedBatch[]> => {
    const result = await requestClient.get(`/production-items/${productionItemId}/seed-batches`);
    const seedBatches = result
      .data as SeedBatch[];
    return seedBatches;
  };

  const updateSeedReservation = async (
    data: UpdateSeedReservationData,
  ): Promise<ProductionItem> => {
    const { id: productionItemId, data: seedReservationData } = data;
    const params: Record<string, unknown> = {
      ...seedReservationData,
    };
    const result = await requestClient.put(`/production-items/${productionItemId}/seed-reservation`, params);
    const updatedSeedingBatch: ProductionItem = result.data as ProductionItem;
    return updatedSeedingBatch;
  };

  const createSeedTicket = async (
    data: CreateSeedTicketData,
  ): Promise<SeedTicket> => {
    const { productionItemId, data: seedTicketData } = data;
    const params: Record<string, unknown> = {
      ...seedTicketData,
    };
    const result = await requestClient.post(`/production-items/${productionItemId}/seed-tickets`, params);
    const seedTicket: SeedTicket = result.data as SeedTicket;
    return seedTicket;
  };

  return {
    getProductionItem,
    getProductionItemList,
    getProductionItemSeedStatus,
    getProductionItemAvailableSeedBatches,
    updateSeedReservation,
    createSeedTicket,
  };
};

export const productionItemApi = productionItemApiBuilder();
